<script setup>
import ModalWrapper from '@/modals/ModalWrapper';
import { ref, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';

const props = defineProps({
    user: {
        type: Object,
        default: () => ({
            role: null,
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            is_active: true,
        })
    },
    mode: {
        type: String,
        default: 'Create'
    }
});

const emit = defineEmits(['close-modal']);
const form = ref({ ...props.user });

const toast = useToast();

import { useOptionsStore } from '@/stores/options';
const optionsStore = useOptionsStore();
const { roleOptions } = storeToRefs(optionsStore);

const userStore = useUserStore();
const { storeUser, updateUser } = userStore;

if (props.user.hash && props.user.roles) {
    const matchedRole = roleOptions.value.find(role => role.id === props.user.roles[0].id);
    if (matchedRole) {
        form.value.role = matchedRole.name;
    }
}

const roleOptionsWithoutMember = computed(() => {
    return roleOptions.value.filter(role => role.name !== 'member');
});

const save = async () => {
    try {
        const action = props.mode === 'Create' ? storeUser : updateUser;
        const toastMessage = props.mode === 'Create'
            ? trans('actions.create.success', { type: trans('label.user') })
            : trans('actions.update.success', { type: trans('label.user') });

        await action({
            ...form.value,
            hash: props.user.hash,
        });

        toast.success(toastMessage);
        emit('close-modal');
    } catch (error) {
        toast.error(trans('actions.unexpected_error'));
    }
};
</script>
<template>
<ModalWrapper>
    <template #header>{{ mode }} {{ $t('label.user') }}</template>
    <template #subheader>{{ $t('label.create_or_edit', { what: mode === 'Create' ? $t('label.user') : form.first_name + ' ' + form.last_name }) }}</template>

    <form @submit.prevent="save">
        <div class="grid md:grid-cols-2 gap-x-4 gap-y-3">
            <StyledInput
                v-model="form.first_name"
                name="first_name"
                :placeholder="$t('label.first_name')"
                :label="$t('label.first_name')" />
            <StyledInput
                v-model="form.last_name"
                :placeholder="$t('label.last_name')"
                :label="$t('label.last_name')"
                name="last_name" />
            <StyledInput
                v-model="form.email"
                name="email"
                :placeholder="$t('label.email')"    
                :label="$t('label.email')" />
            <StyledDropdown
                v-model="form.role"
                item-title="display_name"
                item-value="name"
                :options="mode === 'Create' ? roleOptionsWithoutMember : roleOptions"
                name="role"
                :label="$t('label.role')" 
                :placeholder="$t('label.select_role')" />
            <StyledRoundSwitch
                v-model="form.send_password"
                :label="$t('label.email_password_setup')"
                inset
                name="send_password"
                @input="form.set_password = false" />
            <StyledRoundSwitch
                v-model="form.set_password"
                :label="mode === 'Edit' ? 'Reset Password' : 'Set Password'"
                inset
                name="set_password"
                @input="form.send_password = false" />
            <StyledRoundSwitch
                v-model="form.is_active"
                :label="$t('label.active')"
                inset
                name="is_active" />
        </div>

        <div v-if="form.set_password" class="grid md:grid-cols-2 gap-x-4">
            <StyledPassword
                v-model="form.password"
                name="password"
                :label="$t('label.password')" />
            <StyledPassword
                v-model="form.password_confirmation"
                name="password_confirmation"
                :label="$t('label.confirm_password')" />
        </div>
    </form>

    <template #footer>
        <StyledSaveButton modal @save="save" />
    </template>
</ModalWrapper>
</template>
